/**
 * Phase 1l States — i18n SSOT (ja)
 *
 * Empty / Loading / Error 状態で表示する文字列の Single Source of Truth。
 * TypeScript const で export (framework 不要、Option A)。
 *
 * Issue #2997 / SSOT: design-phase-1l-states.md
 */

interface ErrorMessageEntry {
  readonly title: string;
  readonly description: string;
  readonly cta: string;
}

const errorMessages = {
  network: {
    title: '通信に問題が発生しました',
    description: 'インターネット接続をご確認のうえ、もう一度お試しください。',
    cta: 'もう一度試す',
  },
  notFound: {
    title: 'お探しの情報は見つかりませんでした',
    description: '削除された、または URL が変更された可能性があります。',
    cta: '一覧に戻る',
  },
  validation: {
    title: '入力内容を確認してください',
    description: '必須項目が未入力か、形式が正しくありません。',
    cta: '修正する',
  },
  forbiddenPlan: {
    title: 'この機能はご契約プランではご利用いただけません',
    description:
      'プランをアップグレードすると、この機能を利用できるようになります。',
    cta: 'プランを確認',
  },
  system: {
    title: '予期しないエラーが発生しました',
    description:
      '時間をおいて再度お試しください。問題が続く場合はサポートまでご連絡ください。',
    cta: 'サポートに連絡',
  },
  conflict: {
    title: '予約が重複しています',
    description:
      'その時間はすでに予約が入っています。他の時間をご提案ください。',
    cta: '空き時間を確認',
  },
  dataSaveFail: {
    title: 'データの保存に失敗しました',
    description: '入力した内容が保存されませんでした。もう一度お試しください。',
    cta: '再度保存する',
  },
} as const satisfies Record<string, ErrorMessageEntry>;

export const STATE_MESSAGES = {
  loading: {
    default: '読み込み中...',
    long: 'データを取得しています。しばらくお待ちください...',
  },
  empty: {
    default: {
      title: 'データがありません',
      description: '表示できる情報が見つかりませんでした。',
    },
  },
  error: errorMessages,
  boundary: {
    title: '申し訳ございません',
    description:
      'アプリケーションでエラーが発生しました。\nページを再読み込みしてもう一度お試しください。',
    reloadLabel: 'ページを再読み込み',
    homeLabel: 'ホームへ戻る',
    devDetailsTitle: '開発環境 - エラー詳細',
    devErrorMessageSummary: 'エラーメッセージ',
    devComponentStackSummary: 'コンポーネントスタック',
    helpPrefix: '問題が解決しない場合は、サポートまでお問い合わせください。',
    footerWithSentry: 'エラーは自動的にSentryに報告されています。',
    footerWithoutSentry:
      'エラー情報は記録されていません。サポートへのお問い合わせ時は、上記のエラーメッセージをコピーしてお伝えください。',
  },
} as const;

export type StateMessages = typeof STATE_MESSAGES;
export type ErrorKindKey = keyof typeof STATE_MESSAGES.error;
